import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Modal, TouchableOpacity, TouchableWithoutFeedback } from 'react-native';

import { valueExists } from '../../../common/utils/utils';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../text/Text';
import { InputNumber } from '../input';
import PickerButton from '../picker/PickerButton/PickerButton';
import { createStocktakeQuantitySelectorStyles } from './StocktakeQuantitySelector.styles';

const PACKAGING_TYPES = {
	UNITS: 'units',
	CASES: 'cases',
	SHIPPERS: 'shippers',
	PALLETS: 'pallets',
};

const StocktakeQuantitySelector = ({
	warehouseProduct,
	value,
	onChange,
	disabled = false,
}) => {
	const styles = useThemedStyles(createStocktakeQuantitySelectorStyles);
	const product = warehouseProduct?.product;
	const caseSize = warehouseProduct?.caseSize || product?.caseSize || 1;
	const shipperSize = product?.shipperSize || 0;
	const palletSize = product?.palletSize || 0;
	const forceQuantityInUnits = Boolean(product?.forceQuantityInUnits);

	const packagingAvailability = useMemo(() => ({
		units: true,
		cases: !forceQuantityInUnits && caseSize > 1,
		shippers: !forceQuantityInUnits && shipperSize > 0 && caseSize > 1,
		pallets: !forceQuantityInUnits && palletSize > 0 && caseSize > 1,
	}), [caseSize, forceQuantityInUnits, shipperSize, palletSize]);

	const packagingOptions = useMemo(() => {
		const options = [
			{ label: 'Units', value: PACKAGING_TYPES.UNITS },
		];

		if (packagingAvailability.cases) {
			options.push({
				label: `Cases (${caseSize})`,
				value: PACKAGING_TYPES.CASES,
			});
		}

		if (packagingAvailability.shippers) {
			options.push({
				label: `Shippers (${shipperSize}x${caseSize})`,
				value: PACKAGING_TYPES.SHIPPERS,
			});
		}

		if (packagingAvailability.pallets) {
			options.push({
				label: `Pallets (${palletSize}x${caseSize})`,
				value: PACKAGING_TYPES.PALLETS,
			});
		}

		return options;
	}, [packagingAvailability, caseSize, shipperSize, palletSize]);

	const getDefaultPackagingType = useCallback(() => {
		if (packagingAvailability.pallets) {
			return PACKAGING_TYPES.PALLETS;
		}
		if (packagingAvailability.shippers) {
			return PACKAGING_TYPES.SHIPPERS;
		}
		if (packagingAvailability.cases) {
			return PACKAGING_TYPES.CASES;
		}
		return PACKAGING_TYPES.UNITS;
	}, [packagingAvailability]);

	const convertUnitsForType = useCallback((units, type) => {
		const normalizedUnits = Math.max(0, Number(units) || 0);

		switch (type) {
			case PACKAGING_TYPES.PALLETS:
				if (packagingAvailability.pallets) {
					const palletUnits = palletSize * caseSize;
					const mainQuantity = Math.floor(normalizedUnits / palletUnits);
					const remainder = normalizedUnits % palletUnits;
					return {
						mainQuantity,
						extraCases: Math.floor(remainder / caseSize),
						extraUnits: remainder % caseSize,
					};
				}
				break;
			case PACKAGING_TYPES.SHIPPERS:
				if (packagingAvailability.shippers) {
					const shipperUnits = shipperSize * caseSize;
					const mainQuantity = Math.floor(normalizedUnits / shipperUnits);
					const remainder = normalizedUnits % shipperUnits;
					return {
						mainQuantity,
						extraCases: Math.floor(remainder / caseSize),
						extraUnits: remainder % caseSize,
					};
				}
				break;
			case PACKAGING_TYPES.CASES:
				if (packagingAvailability.cases) {
					return {
						mainQuantity: Math.floor(normalizedUnits / caseSize),
						extraCases: 0,
						extraUnits: normalizedUnits % caseSize,
					};
				}
				break;
		}

		return {
			mainQuantity: normalizedUnits,
			extraCases: 0,
			extraUnits: 0,
		};
	}, [caseSize, shipperSize, palletSize, packagingAvailability]);

	const convertUnitsForDisplay = useCallback((units) => {
		const normalizedUnits = Math.max(0, Number(units) || 0);
		const priorityTypes = [
			PACKAGING_TYPES.PALLETS,
			PACKAGING_TYPES.SHIPPERS,
			PACKAGING_TYPES.CASES,
		];

		if (normalizedUnits === 0) {
			return {
				packagingType: getDefaultPackagingType(),
				mainQuantity: 0,
				extraUnits: 0,
			};
		}

		for (const type of priorityTypes) {
			const { mainQuantity, extraCases, extraUnits } = convertUnitsForType(normalizedUnits, type);
			if (mainQuantity > 0 && packagingAvailability[type]) {
				return {
					packagingType: type,
					mainQuantity,
					extraCases,
					extraUnits,
				};
			}
		}

		return {
			packagingType: PACKAGING_TYPES.UNITS,
			mainQuantity: normalizedUnits,
			extraCases: 0,
			extraUnits: 0,
		};
	}, [convertUnitsForType, getDefaultPackagingType, packagingAvailability]);

	const calculateTotalUnitsWithValues = useCallback((mainQty, extraCasesQty, extraUnitsQty, type) => {
		const main = valueExists(mainQty) ? mainQty : 0;
		const extraCases = valueExists(extraCasesQty) ? extraCasesQty : 0;
		const extraUnits = valueExists(extraUnitsQty) ? extraUnitsQty : 0;

		switch (type) {
			case PACKAGING_TYPES.PALLETS:
				if (packagingAvailability.pallets) {
					return (main * palletSize * caseSize) + (extraCases * caseSize) + extraUnits;
				}
				break;
			case PACKAGING_TYPES.SHIPPERS:
				if (packagingAvailability.shippers) {
					return (main * shipperSize * caseSize) + (extraCases * caseSize) + extraUnits;
				}
				break;
			case PACKAGING_TYPES.CASES:
				if (packagingAvailability.cases) {
					return (main * caseSize) + extraUnits;
				}
				break;
			default:
				return main;
		}

		return main;
	}, [caseSize, shipperSize, palletSize, packagingAvailability]);

	const [packagingType, setPackagingType] = useState(getDefaultPackagingType());
	const [mainQuantity, setMainQuantity] = useState(null);
	const [extraCases, setExtraCases] = useState(null);
	const [extraUnits, setExtraUnits] = useState(null);
	const [activeInputField, setActiveInputField] = useState(null); // prevents UI from resetting while typing
	const [isPackagingPickerOpen, setIsPackagingPickerOpen] = useState(false);

	const hasExistingValue = valueExists(value);

	const syncInputsFromValue = useCallback(() => {
		if (!valueExists(value)) {
			setMainQuantity(null);
			setExtraCases(null);
			setExtraUnits(null);
			setPackagingType(getDefaultPackagingType());
			return;
		}

		const breakdown = convertUnitsForDisplay(value);
		setPackagingType(breakdown.packagingType);
		setMainQuantity(breakdown.mainQuantity);
		setExtraCases(breakdown.extraCases);
		setExtraUnits(breakdown.extraUnits);
	}, [convertUnitsForDisplay, getDefaultPackagingType, value]);

	useEffect(() => {
		if (!activeInputField) {
			syncInputsFromValue();
		}
	}, [activeInputField, syncInputsFromValue]);

	const emitChange = useCallback((units) => {
		if (valueExists(units)) {
			onChange?.(Math.max(0, Number(units) || 0));
		}
		else {
			onChange?.(null);
		}
	}, [onChange]);

	const handleMainQuantityChange = useCallback((newValue) => {
		if (newValue === '' || newValue === null || Number.isNaN(newValue)) {
			setMainQuantity(null);
			const hasRemainders = valueExists(extraCases) || valueExists(extraUnits);
			if (hasRemainders) {
				const total = calculateTotalUnitsWithValues(null, extraCases, extraUnits, packagingType);
				emitChange(total);
			}
			else {
				emitChange(null);
			}
			return;
		}

		const sanitized = Math.max(0, Number(newValue) || 0);
		setMainQuantity(sanitized);
		const total = calculateTotalUnitsWithValues(sanitized, extraCases, extraUnits, packagingType);
		emitChange(total);
	}, [calculateTotalUnitsWithValues, emitChange, extraCases, extraUnits, packagingType]);

	const handleExtraCasesChange = useCallback((newValue) => {
		if (newValue === '' || newValue === null || Number.isNaN(newValue)) {
			setExtraCases(null);
			const hasOtherValues = valueExists(mainQuantity) || valueExists(extraUnits);
			if (hasOtherValues) {
				const total = calculateTotalUnitsWithValues(mainQuantity, null, extraUnits, packagingType);
				emitChange(total);
			}
			else {
				emitChange(null);
			}
			return;
		}

		const sanitized = Math.max(0, Number(newValue) || 0);
		setExtraCases(sanitized);
		const total = calculateTotalUnitsWithValues(mainQuantity, sanitized, extraUnits, packagingType);
		emitChange(total);
	}, [calculateTotalUnitsWithValues, emitChange, extraUnits, mainQuantity, packagingType]);

	const handleExtraUnitsChange = useCallback((newValue) => {
		if (newValue === '' || newValue === null || Number.isNaN(newValue)) {
			setExtraUnits(null);
			const hasOtherValues = valueExists(mainQuantity) || valueExists(extraCases);
			if (hasOtherValues) {
				const total = calculateTotalUnitsWithValues(mainQuantity, extraCases, null, packagingType);
				emitChange(total);
			}
			else {
				emitChange(null);
			}
			return;
		}

		const sanitized = Math.max(0, Number(newValue) || 0);
		setExtraUnits(sanitized);
		const total = calculateTotalUnitsWithValues(mainQuantity, extraCases, sanitized, packagingType);
		emitChange(total);
	}, [calculateTotalUnitsWithValues, emitChange, extraCases, mainQuantity, packagingType]);

	const handlePackagingTypeChange = useCallback((nextType) => {
		setActiveInputField(null);
		setPackagingType(nextType);

		if (!valueExists(value)) {
			setMainQuantity(null);
			setExtraCases(null);
			setExtraUnits(null);
			return;
		}

		const breakdown = convertUnitsForType(value, nextType);
		setMainQuantity(breakdown.mainQuantity);
		setExtraCases(breakdown.extraCases);
		setExtraUnits(breakdown.extraUnits);
	}, [convertUnitsForType, value]);

	const selectedPackagingLabel = useMemo(() => {
		return packagingOptions.find(option => option.value === packagingType)?.label || 'Select packaging';
	}, [packagingOptions, packagingType]);

	const canChangePackaging = !disabled && packagingOptions.length > 1;

	const openPackagingPicker = useCallback(() => {
		if (canChangePackaging) {
			setIsPackagingPickerOpen(true);
		}
	}, [canChangePackaging]);

	const closePackagingPicker = useCallback(() => {
		setIsPackagingPickerOpen(false);
	}, []);

	const handlePackagingOptionPress = useCallback((optionValue) => {
		setIsPackagingPickerOpen(false);
		handlePackagingTypeChange(optionValue);
	}, [handlePackagingTypeChange]);

	const handleInputFocus = useCallback((fieldKey) => () => {
		setActiveInputField(fieldKey);
	}, []);

	const handleInputBlur = useCallback(() => {
		setActiveInputField(null);
		syncInputsFromValue();
	}, [syncInputsFromValue]);

	const renderInputField = (label, inputValue, onChangeInternal, options = {}) => {
		const { autoFocus = false, containerStyle, fieldKey } = options;
		return (
			<View style={[styles.inputContainer, containerStyle]}>
				<Text style={styles.inputLabel}>{label}</Text>
				<InputNumber
					value={valueExists(inputValue) ? inputValue : null}
					onChangeInternal={onChangeInternal}
					disabled={disabled}
					decimalPlaces={0}
					autoFocus={autoFocus}
					textAlign="left"
					nullable={true}
					onFocus={fieldKey ? handleInputFocus(fieldKey) : undefined}
					onBlur={handleInputBlur}
				/>
			</View>
		);
	};

	const primaryLabel = useMemo(() => {
		switch (packagingType) {
			case PACKAGING_TYPES.CASES:
				return 'Cases';
			case PACKAGING_TYPES.SHIPPERS:
				return 'Shippers';
			case PACKAGING_TYPES.PALLETS:
				return 'Pallets';
			default:
				return 'Units';
		}
	}, [packagingType]);

	const isUnitsOnly = packagingType === PACKAGING_TYPES.UNITS;
	const isCases = packagingType === PACKAGING_TYPES.CASES;
	const isMultiTier = packagingType === PACKAGING_TYPES.SHIPPERS || packagingType === PACKAGING_TYPES.PALLETS;

	return (
		<View style={styles.container}>
			<Text style={styles.label}>Packaging</Text>
			<PickerButton
				onPress={openPackagingPicker}
				disabled={!canChangePackaging}
				style={styles.packagingPickerButton}
			>
				{selectedPackagingLabel}
			</PickerButton>
			<Modal
				visible={isPackagingPickerOpen}
				transparent={true}
				animationType="fade"
				onRequestClose={closePackagingPicker}
			>
				<View style={styles.packagingPickerModalBackdrop}>
					<TouchableWithoutFeedback onPress={closePackagingPicker}>
						<View style={styles.packagingPickerModalOverlay} />
					</TouchableWithoutFeedback>
					<View style={styles.packagingPickerModalContent}>
						{
							packagingOptions.map(option => (
								<TouchableOpacity
									key={option.value}
									style={styles.packagingPickerOption}
									onPress={() => handlePackagingOptionPress(option.value)}
								>
									<Text style={[
										styles.packagingPickerOptionText,
										option.value === packagingType && styles.packagingPickerOptionTextSelected,
									]}
									>
										{option.label}
									</Text>
								</TouchableOpacity>
							))
						}
					</View>
				</View>
			</Modal>

			<View style={styles.inputsRow}>
				{renderInputField(primaryLabel, mainQuantity, handleMainQuantityChange, {
					autoFocus: !hasExistingValue,
					containerStyle: (!isUnitsOnly) ? styles.inputContainerSpacing : null,
					fieldKey: 'mainQuantity',
				})}

				{isCases && renderInputField('Units', extraUnits, handleExtraUnitsChange, {
					fieldKey: 'extraUnits',
				})}

				{isMultiTier && (
					<>
						{renderInputField('Cases', extraCases, handleExtraCasesChange, {
							containerStyle: styles.inputContainerSpacing,
							fieldKey: 'extraCases',
						})}
						{renderInputField('Units', extraUnits, handleExtraUnitsChange, {
							fieldKey: 'extraUnits',
						})}
					</>
				)}
			</View>
		</View>
	);
};

export default StocktakeQuantitySelector;
